import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Send, MessageCircle, User, Clock, CheckCheck, Check, Phone, Settings, Zap, FileText, Trash2, RotateCcw, ChevronDown, Sparkles, Loader2, Mic, Square, Languages, Paperclip, UserPlus, MoreVertical, Pencil, Hash, CalendarClock, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editText, setEditText] = useState("");
  const [serviceWindow, setServiceWindow] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [chatStatus, setChatStatus] = useState("active"); // active, archived, deleted
  const [sending, setSending] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState({ enabled: false, configured: false });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    phone_id: "",
    access_token: "",
    verify_token: "strongfit_webhook_2025",
    business_account_id: "",
    enabled: false,
    ai_auto_reply_enabled: false
  });
  // Store actual credential values separately (not masked)
  const [actualCredentials, setActualCredentials] = useState({
    phone_id: "",
    access_token: ""
  });
  const [metaConfig, setMetaConfig] = useState({ configured: false, app_id: null });
  const [connecting, setConnecting] = useState(false);
  const [setupWizardOpen, setSetupWizardOpen] = useState(false);
  const [setupStep, setSetupStep] = useState(1);
  const [tempAppId, setTempAppId] = useState("");
  const [tempAppSecret, setTempAppSecret] = useState("");
  const [sendTemplateOpen, setSendTemplateOpen] = useState(false);
  const [onbInviteOpen, setOnbInviteOpen] = useState(false);
  const [onbInvite, setOnbInvite] = useState({ phone: '', name: '', studio: '', sender: '', personalize: false });
  const [onbSending, setOnbSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedTemplateObj, setSelectedTemplateObj] = useState(null);
  const [recipientPhone, setRecipientPhone] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [suspendCustomerSearch, setSuspendCustomerSearch] = useState(false);
  const [templateParameters, setTemplateParameters] = useState([]); // Variablenwerte für Template
  const [botEnabled, setBotEnabled] = useState(true);
  const [botPausedInfo, setBotPausedInfo] = useState(null);
  const [botStatusLoading, setBotStatusLoading] = useState(false);
  const [adminTakeoverUntil, setAdminTakeoverUntil] = useState(null);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const [pendingNewMessages, setPendingNewMessages] = useState(false);
  
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const previousMessagesSignatureRef = useRef("");

  const getMessagesViewport = () => {
    if (!scrollAreaRef.current) return null;
    return scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
  };

  const isViewportNearBottom = (viewport) => {
    if (!viewport) return true;
    const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    return distanceFromBottom < 120;
  };

  const getMessagesSignature = (list = []) => {
    const last = list[list.length - 1];
    const lastIdentifier = last?.id || last?.message_id || last?.timestamp || "";
    const lastStatus = last?.status || "";
    const lastContent = last?.content || last?.body || "";
    return `${list.length}-${lastIdentifier}-${lastStatus}-${lastContent}`;
  };
  
  const scrollToBottom = (behavior = "smooth") => {
    // Try multiple scroll methods for compatibility
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior, block: "end" });
    }
    // Also try to scroll the container directly
    const viewport = getMessagesViewport();
    if (viewport) {
      viewport.scrollTo({ top: viewport.scrollHeight, behavior });
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchWhatsAppStatus();
    fetchSettings();
    fetchMetaConfig();
    fetchChats();
  }, [chatStatus]);

  // Use a ref to track the current selected chat for the polling interval
  const selectedChatRef = useRef(null);
  
  // Keep the ref updated
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    shouldAutoScrollRef.current = true;
    previousMessagesSignatureRef.current = "";
    setShowScrollToLatest(false);
    setPendingNewMessages(false);
  }, [selectedChat?.chat_id]);

  // Polling for new messages - runs every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) {
        return;
      }
      // Use ref to get the CURRENT value of selectedChat
      if (selectedChatRef.current && selectedChatRef.current.chat_id) {
        fetchMessages(selectedChatRef.current.chat_id, true);
      }
      fetchChats(true);
    }, 3000); // Reduced to 3 seconds for faster updates
    
    return () => clearInterval(interval);
  }, []); // Empty dependency - only create once

  // Function to select a chat and load its messages
  const handleChatSelect = async (chatId) => {
    try {
      shouldAutoScrollRef.current = true;

      // URL-encode the chatId to handle + signs properly
      const encodedChatId = encodeURIComponent(chatId);

      await fetchMessages(chatId);
      
      // Mark as read
      await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedChatId}/mark-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh chat list to update badges
      fetchChats(true);

      setBotEnabled(true);
      await fetchBotStatus(chatId);
    } catch (error) {
      console.error("Error loading chat:", error);
    }
  };

  // Messages are now loaded directly in selectChat function
  // This effect is kept for polling updates only
  useEffect(() => {
    // Only refresh if chat is already selected (for polling)
    // Initial load is handled by selectChat
  }, [selectedChat?.chat_id]);

  useEffect(() => {
    if (!selectedChat?.chat_id) return;

    const viewport = getMessagesViewport();
    if (!viewport) return;

    const handleScroll = () => {
      const isNearBottom = isViewportNearBottom(viewport);
      shouldAutoScrollRef.current = isNearBottom;
      setShowScrollToLatest(!isNearBottom);

      if (isNearBottom) {
        setPendingNewMessages(false);
      }
    };

    handleScroll();
    viewport.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
    };
  }, [selectedChat?.chat_id]);

  useEffect(() => {
    const currentSignature = getMessagesSignature(messages);
    const previousSignature = previousMessagesSignatureRef.current;

    if (previousSignature && currentSignature !== previousSignature && !shouldAutoScrollRef.current) {
      setPendingNewMessages(true);
      setShowScrollToLatest(true);
    }

    previousMessagesSignatureRef.current = currentSignature;

    if (!messages.length || !shouldAutoScrollRef.current) {
      return;
    }

    setPendingNewMessages(false);
    setShowScrollToLatest(false);

    // Small delay to ensure DOM is updated before scrolling
    const timer = setTimeout(() => {
      scrollToBottom("auto");
    }, 100);

    return () => clearTimeout(timer);
  }, [messages]);

  const jumpToLatestMessages = () => {
    shouldAutoScrollRef.current = true;
    setPendingNewMessages(false);
    setShowScrollToLatest(false);
    scrollToBottom("smooth");
  };

  const getMsgId = (msg) => msg.id || msg.message_id;

  const deleteMessage = async (msg) => {
    const id = getMsgId(msg);
    if (!id) return;
    if (!window.confirm("Diese Nachricht endgültig aus dem Verlauf entfernen?\n\nHinweis: WhatsApp kann eine bereits gesendete Nachricht nicht beim Kunden zurückrufen – sie wird nur hier im Chat-Verlauf entfernt.")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/messages/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setMessages((prev) => prev.filter((m) => getMsgId(m) !== id));
      toast.success("Nachricht gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const startEditMessage = (msg) => {
    setEditingMsgId(getMsgId(msg));
    setEditText(msg.content || msg.body || "");
  };

  const cancelEditMessage = () => {
    setEditingMsgId(null);
    setEditText("");
  };

  const saveEditMessage = async (msg) => {
    const id = getMsgId(msg);
    const content = editText.trim();
    if (!content) {
      toast.error("Text darf nicht leer sein");
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/messages/${encodeURIComponent(id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error();
      setMessages((prev) => prev.map((m) => (getMsgId(m) === id ? { ...m, content, body: content, edited: true } : m)));
      cancelEditMessage();
      toast.success("Nachricht bearbeitet");
    } catch {
      toast.error("Bearbeiten fehlgeschlagen");
    }
  };

  const fetchWhatsAppStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWhatsappStatus(data);
      }
    } catch (error) {
      console.error("Error fetching WhatsApp status:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const toggleOutbound = async (next) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/settings/toggle-sending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: next })
      });
      if (response.ok) {
        setSettings((s) => ({ ...s, outbound_enabled: next }));
        toast.success(next ? 'WhatsApp-Versand EINGESCHALTET' : 'WhatsApp-Versand AUSGESCHALTET – es werden keine WhatsApps mehr versendet');
      } else {
        toast.error('Umschalten fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Umschalten fehlgeschlagen');
    }
  };

  const fetchMetaConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/oauth/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMetaConfig(data);
        
        // Load Facebook SDK with App ID if configured
        if (data.configured && data.app_id) {
          loadFacebookSDK(data.app_id, data.graph_version);
        }
      }
    } catch (error) {
      console.error("Error fetching Meta config:", error);
    }
  };

  const loadFacebookSDK = (appId, version) => {
    const v = version || 'v23.0';
    // Remove existing SDK if present
    const existingScript = document.getElementById('facebook-jssdk');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Remove FB object
    if (window.FB) {
      delete window.FB;
    }
    
    if (!appId) {
      console.log("No App ID provided, skipping FB SDK load");
      return;
    }

    // Load Facebook SDK
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: v
      });
      console.log("✅ Facebook SDK initialized with App ID:", appId);
    };

    // Load SDK script
    (function(d, s, id){
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {return;}
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  };

  const handleConnectClick = () => {
    if (!metaConfig.configured || !metaConfig.app_id) {
      // Meta App nicht konfiguriert - Setup Wizard öffnen
      setSetupWizardOpen(true);
      setSetupStep(1);
      return;
    }

    // Meta App konfiguriert - direkt verbinden
    launchMetaConnect();
  };

  const launchMetaConnect = () => {
    if (!window.FB) {
      toast.error("Facebook SDK nicht geladen. Bitte Seite neu laden.");
      return;
    }

    setConnecting(true);

    const FB_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com'];
    let captured = {};
    let codeVal = null;
    let sent = false;

    const onMsg = (event) => {
      if (!FB_ORIGINS.includes(event.origin)) return;
      let d;
      try { d = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch (e) { return; }
      if (!d || d.type !== 'WA_EMBEDDED_SIGNUP') return;
      if (d.data) {
        captured = {
          waba_id: d.data.waba_id,
          phone_number_id: d.data.phone_number_id,
          business_id: d.data.business_id,
        };
      }
    };

    const trySend = () => {
      if (sent || !codeVal) return;
      sent = true;
      window.removeEventListener('message', onMsg);
      sendOAuthToBackend(codeVal, captured.waba_id || '', captured.phone_number_id || '', captured.business_id || '');
    };

    // Listener MUSS vor FB.login registriert sein (FINISH kann vorher eintreffen)
    window.addEventListener('message', onMsg);

    const opts = { response_type: 'code', override_default_response_type: true };
    if (metaConfig.config_id) {
      opts.config_id = metaConfig.config_id;
    } else {
      opts.scope = 'whatsapp_business_management,whatsapp_business_messaging';
      opts.extras = { feature: 'whatsapp_embedded_signup', version: 2 };
    }

    window.FB.login(function(response) {
      if (response && response.authResponse && response.authResponse.code) {
        codeVal = response.authResponse.code;
        // Kurz auf postMessage-IDs warten, dann in jedem Fall senden (Backend ermittelt WABA notfalls selbst)
        setTimeout(trySend, 1200);
      } else {
        setConnecting(false);
        window.removeEventListener('message', onMsg);
        toast.error("WhatsApp-Verbindung abgebrochen");
      }
    }, opts);
  };

  const saveMetaCredentials = async () => {
    if (!tempAppId.trim() || !tempAppSecret.trim()) {
      toast.error("Bitte beide Felder ausfüllen");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/meta-credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          app_id: tempAppId,
          app_secret: tempAppSecret
        })
      });

      if (response.ok) {
        toast.success("Meta App Credentials gespeichert!");
        
        // Fetch new meta config and load FB SDK
        await fetchMetaConfig();
        
        // Give SDK time to load
        setTimeout(() => {
          setSetupStep(3);
        }, 1000);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
      console.error("Error saving credentials:", error);
    }
  };

  const completeSetup = () => {
    setSetupWizardOpen(false);
    setSetupStep(1);
    setTempAppId("");
    setTempAppSecret("");
    
    // Check if FB SDK is loaded
    if (!window.FB) {
      toast.error("Facebook SDK lädt noch. Bitte warten Sie 2 Sekunden und versuchen Sie es erneut.");
      return;
    }
    
    // Launch Meta Connect
    setTimeout(() => {
      launchMetaConnect();
    }, 500);
  };

  const handleOAuthCallback = async (code, wabaId) => {
    try {
      // Listen for postMessage from Facebook popup
      const messageHandler = (event) => {
        if (event.origin !== "https://www.facebook.com") return;
        
        const data = event.data;
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          // Extract data from Meta response
          const phoneNumberId = data.phone_number_id;
          const wabaId = data.waba_id;
          const businessId = data.business_id;
          
          // Send to backend
          sendOAuthToBackend(code, wabaId, phoneNumberId, businessId);
        }
      };

      window.addEventListener('message', messageHandler);

      // Fallback: If no postMessage received, try with available data
      setTimeout(() => {
        window.removeEventListener('message', messageHandler);
        if (connecting) {
          toast.info("Bitte warten, WhatsApp wird konfiguriert...");
        }
      }, 5000);

    } catch (error) {
      console.error("Error handling OAuth callback:", error);
      setConnecting(false);
      toast.error("Fehler bei der Verbindung");
    }
  };

  const sendOAuthToBackend = async (code, wabaId, phoneNumberId, businessId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/oauth/callback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          code,
          waba_id: wabaId,
          phone_number_id: phoneNumberId,
          business_id: businessId
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "WhatsApp erfolgreich verbunden!");
        fetchWhatsAppStatus();
        fetchSettings();
        setConnecting(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler bei der Verbindung");
        setConnecting(false);
      }
    } catch (error) {
      console.error("Error sending OAuth to backend:", error);
      toast.error("Fehler bei der Verbindung");
      setConnecting(false);
    }
  };

  const saveSettings = async () => {
    try {
      // First save Meta App Credentials if provided
      // Platzhalter '***SET***' nie als echtes Secret senden (Backend blockt zusätzlich)
      if (settings.meta_app_id && settings.meta_app_secret && settings.meta_app_secret !== '***SET***') {
        const metaResponse = await fetch(`${BACKEND_URL}/api/whatsapp/meta-credentials`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            app_id: settings.meta_app_id,
            app_secret: settings.meta_app_secret
          })
        });

        if (!metaResponse.ok) {
          const error = await metaResponse.json();
          toast.error("Fehler beim Speichern der Meta App Credentials: " + (error.detail || ""));
          return;
        }
      }

      // Then save WhatsApp settings
      // Use actual credentials if available, otherwise use masked values
      const phoneIdToSend = actualCredentials.phone_id || settings.phone_id || "";
      const accessTokenToSend = actualCredentials.access_token || settings.access_token || "";
      
      const params = new URLSearchParams({
        phone_id: phoneIdToSend,
        access_token: accessTokenToSend,
        verify_token: settings.verify_token,
        business_account_id: settings.business_account_id || "",
        enabled: settings.enabled.toString(),
        ai_auto_reply_enabled: settings.ai_auto_reply_enabled.toString()
      });

      const response = await fetch(`${BACKEND_URL}/api/whatsapp/settings?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("WhatsApp-Einstellungen gespeichert! Bitte warten Sie 5 Sekunden...");
        
        // Wait for backend restart
        setTimeout(() => {
          setSettingsOpen(false);
          fetchWhatsAppStatus();
          fetchSettings();
          fetchMetaConfig();
          toast.success("Einstellungen aktiv!");
        }, 5000);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern der Einstellungen");
      console.error("Error saving settings:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Nur genehmigte UND in der App freigegebene Templates (app_visible)
        const approvedTemplates = (data.data || []).filter(t => t.status === 'APPROVED' && t.app_visible);
        setTemplates(approvedTemplates);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };



  const searchCustomers = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerResults([]);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/customers?search=${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomerResults((data || []).slice(0, 5));
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  }, [token]);

  useEffect(() => {
    if (suspendCustomerSearch) {
      setSuspendCustomerSearch(false);
      return;
    }

    const handler = setTimeout(() => {
      searchCustomers(customerSearch);
    }, 300);

    return () => clearTimeout(handler);
  }, [customerSearch, searchCustomers, suspendCustomerSearch]);

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSuspendCustomerSearch(true);
    setCustomerSearch(`${customer.first_name} ${customer.last_name}`);
    setRecipientPhone(customer.phone || "");
    setCustomerResults([]);
  };
  
  // Extrahiere Variablen aus Template und initialisiere Parameter-Array
  const handleTemplateChange = (templateName) => {
    setSelectedTemplate(templateName);
    
    const template = templates.find(t => t.name === templateName);
    setSelectedTemplateObj(template);
    
    if (template && template.components) {
      // Zähle Variablen im Body
      const bodyComponent = template.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        const matches = bodyComponent.text.match(/\{\{\d+\}\}/g);
        const varCount = matches ? matches.length : 0;
        // Initialisiere Parameter-Array
        setTemplateParameters(new Array(varCount).fill(''));
      } else {
        setTemplateParameters([]);
      }
    } else {
      setTemplateParameters([]);
    }
  };

  const sendTemplateMessage = async () => {
    if (!selectedTemplate || !recipientPhone) {
      toast.error("Bitte wählen Sie ein Template und geben Sie eine Telefonnummer ein");
      return;
    }

    // Onboarding-Vorlage: Link/Studio/Absender werden serverseitig automatisch
    // gesetzt, nur der Name (Variable 1) ist optional.
    const _obj = templates.find(t => t.name === selectedTemplate);
    const _isOnb = _obj && (((_obj.name || '').toLowerCase().includes('onboarding')) ||
      (_obj.components || []).some(c => c.type === 'BUTTONS' && (c.buttons || []).some(b => (b.url || '').includes('/onboarding/'))));
    if (_isOnb) {
      try {
        setSending(true);
        const res = await fetch(`${BACKEND_URL}/api/whatsapp/send-onboarding-invite`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            recipient_phone: recipientPhone,
            customer_name: (templateParameters[0] || '').trim(),
            studio_name: (templateParameters[1] || '').trim(),
            sender_name: (templateParameters[2] || '').trim(),
            template_name: selectedTemplate,
            template_language: _obj?.language || 'de',
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || 'Fehler');
        toast.success("✅ Onboarding-Einladung gesendet!");
        setSendTemplateOpen(false);
        setSelectedTemplate(""); setSelectedTemplateObj(null); setRecipientPhone("");
        setSelectedCustomer(null); setCustomerSearch(""); setTemplateParameters([]);
        fetchChats(true);
      } catch (e) {
        toast.error(`❌ ${e.message || 'Senden fehlgeschlagen'}`);
      } finally {
        setSending(false);
      }
      return;
    }

    // Prüfe ob alle Variablen ausgefüllt sind
    if (templateParameters.length > 0 && templateParameters.some(p => !p.trim())) {
      toast.error("Bitte füllen Sie alle Variablen aus");
      return;
    }

    // Finde das ausgewählte Template, um die richtige Sprache zu verwenden
    const template = templates.find(t => t.name === selectedTemplate);
    const templateLanguage = template?.language || "de";
    
    // Konvertiere Parameter in das richtige Format für WhatsApp API
    const formattedParameters = templateParameters.map(value => ({
      type: "text",
      text: value
    }));

    try {
      setSending(true);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/templates/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_phone: recipientPhone,
          template_name: selectedTemplate,
          template_language: templateLanguage,
          template_parameters: formattedParameters  // Jetzt im richtigen Format
        })
      });

      if (response.ok) {
        toast.success("✅ Template-Nachricht gesendet!");
        setSendTemplateOpen(false);
        setSelectedTemplate("");
        setSelectedTemplateObj(null);
        setRecipientPhone("");
        setSelectedCustomer(null);
        setCustomerSearch("");
        setTemplateParameters([]);
        fetchChats(true);
      } else {
        const error = await response.json();
        toast.error(`❌ Fehler: ${error.detail}`);
      }
    } catch (error) {
      toast.error("Fehler beim Senden");
      console.error("Error sending template:", error);
    } finally {
      setSending(false);
    }
  };


  const fetchChats = async (silent = false) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats?status=${chatStatus}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChats(data.chats || []);
      }
    } catch (error) {
      if (!silent) {
        console.error("Error fetching chats:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const archiveChat = async (phoneNumber) => {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedPhone}/archive`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Chat als erledigt markiert");
        fetchChats(true); // Refresh list
        const selectedPhone = selectedChat?.chat_id || selectedChat?.from_phone;
        if (selectedPhone === phoneNumber) {
          setSelectedChat(null);
          setMessages([]);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Archivieren");
      }
    } catch (error) {
      console.error("Error archiving chat:", error);
      toast.error("Fehler beim Archivieren");
    }
  };

  const deleteChat = async (phoneNumber) => {
    if (!confirm("Chat in Papierkorb verschieben? (Kann wiederhergestellt werden)")) {
      return;
    }
    
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedPhone}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Chat in Papierkorb verschoben");
        fetchChats(true); // Refresh list
        // Check if the deleted chat is currently selected
        const selectedPhone = selectedChat?.chat_id || selectedChat?.from_phone;
        if (selectedPhone === phoneNumber) {
          setSelectedChat(null);
          setMessages([]);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Löschen");
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const restoreChat = async (phoneNumber, fromDeleted = false) => {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedPhone}/restore?from_deleted=${fromDeleted}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(fromDeleted ? "Chat wiederhergestellt" : "Chat reaktiviert");
        fetchChats(true); // Refresh list
      }
    } catch (error) {
      console.error("Error restoring chat:", error);
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  // 07/2026: Fehlgeschlagene Sprachnachrichten — Anhören + erneut transkribieren
  const [audioUrls, setAudioUrls] = useState({});
  const [audioLoading, setAudioLoading] = useState(null);
  const [retranscribing, setRetranscribing] = useState(null);

  const loadVoiceAudio = async (messageId) => {
    setAudioLoading(messageId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/messages/${messageId}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Audio konnte nicht geladen werden');
        return;
      }
      const blob = await res.blob();
      setAudioUrls((prev) => ({ ...prev, [messageId]: URL.createObjectURL(blob) }));
    } catch {
      toast.error('Audio konnte nicht geladen werden');
    } finally {
      setAudioLoading(null);
    }
  };

  const retranscribeMessage = async (messageId) => {
    setRetranscribing(messageId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/messages/${messageId}/retranscribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success('Transkription erfolgreich: ' + (data.transcript || '').slice(0, 60));
        if (selectedChat?.chat_id) fetchMessages(selectedChat.chat_id, true);
      } else {
        toast.error(data.detail || 'Transkription erneut fehlgeschlagen');
      }
    } catch {
      toast.error('Transkription erneut fehlgeschlagen');
    } finally {
      setRetranscribing(null);
    }
  };

  const fetchMessages = async (phoneNumber, silent = false) => {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedPhone}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const incomingMessages = data.messages || [];
        setServiceWindow(data.service_window || null);
        setMessages((previousMessages) => {
          const previousSignature = getMessagesSignature(previousMessages);
          const incomingSignature = getMessagesSignature(incomingMessages);

          if (previousSignature === incomingSignature) {
            return previousMessages;
          }

          return incomingMessages;
        });
      }
    } catch (error) {
      if (!silent) {
        console.error("Error fetching messages:", error);
      }
    }
  };

  const markMessagesAsRead = async (phoneNumber) => {
    try {
      const encodedPhone = encodeURIComponent(phoneNumber);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedPhone}/mark-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Refresh chat list to update unread counts
        fetchChats(true);
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const fetchBotStatus = async (phoneNumber) => {
    if (!phoneNumber) return;
    try {
      setBotStatusLoading(true);
      const encodedPhone = encodeURIComponent(phoneNumber);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedPhone}/bot-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBotEnabled(data.enabled !== false);
        setBotPausedInfo(data.enabled === false && data.paused_by ? data : null);
        setAdminTakeoverUntil(data.admin_takeover_active ? data.admin_active_until : null);
      }
    } catch (error) {
      console.error("Error fetching bot status:", error);
    } finally {
      setBotStatusLoading(false);
    }
  };

  const toggleBotStatus = async () => {
    if (!selectedChat?.chat_id) return;
    const nextStatus = !botEnabled;
    try {
      setBotStatusLoading(true);
      const encodedPhone = encodeURIComponent(selectedChat.chat_id);
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/chats/${encodedPhone}/bot-status`, {
        method: 'PUT',
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: nextStatus })
      });
      if (response.ok) {
        setBotEnabled(nextStatus);
        setBotPausedInfo(null);
        toast.success(nextStatus ? "Bot aktiviert" : "Bot pausiert");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Bot-Status");
      }
    } catch (error) {
      console.error("Error updating bot status:", error);
      toast.error("Fehler beim Bot-Status");
    } finally {
      setBotStatusLoading(false);
    }
  };

  const [improving, setImproving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [pendingAudio, setPendingAudio] = useState(null); // { blob, url }
  const [sendingVoice, setSendingVoice] = useState(false);

  const discardPendingAudio = () => {
    if (pendingAudio?.url) URL.revokeObjectURL(pendingAudio.url);
    setPendingAudio(null);
  };

  const sendVoiceMessage = async () => {
    if (!pendingAudio || !selectedChat) return;
    setSendingVoice(true);
    shouldAutoScrollRef.current = true;
    try {
      const authToken = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", pendingAudio.blob, "sprachnachricht.webm");
      fd.append("phone_number", selectedChat.chat_id);
      fd.append("caption", "");
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/send-media`, {
        method: "POST", headers: { Authorization: `Bearer ${authToken}` }, body: fd,
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.status === "sent") {
        discardPendingAudio();
        fetchMessages(selectedChat.chat_id);
        fetchBotStatus(selectedChat.chat_id);
        toast.success("Sprachnachricht gesendet");
      } else if (response.ok && result.status === "preview_blocked") {
        discardPendingAudio();
        fetchMessages(selectedChat.chat_id);
        toast.info("Vorschau: Sprachnachricht gespeichert & angezeigt – echter Versand ist im Test-System deaktiviert.");
      } else if (response.ok && result.status === "outbound_disabled") {
        toast.error("WhatsApp-Versand ist ausgeschaltet – Sprachnachricht wurde nicht gesendet.");
      } else {
        toast.error(result.error || result.detail || "Fehler beim Senden der Sprachnachricht");
      }
    } catch (e) {
      toast.error("Fehler beim Senden der Sprachnachricht");
      console.error("Error sending voice:", e);
    } finally {
      setSendingVoice(false);
    }
  };

  const transcribePendingAudio = async () => {
    if (!pendingAudio) return;
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingAudio.blob, "aufnahme.webm");
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/transcribe-audio`, {
        method: "POST", headers: { Authorization: `Bearer ${authToken}` }, body: fd,
      });
      if (!response.ok) throw new Error("Transkription fehlgeschlagen");
      const data = await response.json();
      setNewMessage((prev) => (prev.trim() ? `${prev.trim()} ${data.text}` : data.text));
      discardPendingAudio();
      toast.success("🎤 In Text umgewandelt – prüfen, ggf. ✨ verbessern oder 🌐 übersetzen");
    } catch (e) {
      toast.error("Transkription fehlgeschlagen");
    } finally {
      setTranscribing(false);
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) { toast.error('Aufnahme zu kurz'); return; }
        const url = URL.createObjectURL(blob);
        setPendingAudio({ blob, url });
        toast.info('Aufnahme fertig – als Sprachnachricht senden oder in Text umwandeln');
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      toast.info('🎙️ Aufnahme läuft – zum Beenden erneut klicken');
    } catch (e) {
      toast.error('Mikrofon-Zugriff verweigert');
    }
  };

  const translateText = async (targetLanguage) => {
    if (!newMessage.trim() || translating) return;
    const original = newMessage;
    setTranslating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/translate-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: original, target_language: targetLanguage })
      });
      if (!response.ok) throw new Error('Übersetzung fehlgeschlagen');
      const data = await response.json();
      setNewMessage(data.translated_text);
      toast.success(`🌐 Übersetzt auf ${targetLanguage} – bitte prüfen, dann senden`, {
        duration: 8000,
        action: { label: 'Rückgängig', onClick: () => setNewMessage(original) },
      });
    } catch (e) {
      toast.error('Übersetzung momentan nicht verfügbar');
    } finally {
      setTranslating(false);
    }
  };

  const improveText = async () => {
    if (!newMessage.trim() || improving) return;
    const original = newMessage;
    setImproving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/improve-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: original })
      });
      if (!response.ok) throw new Error('KI-Korrektur fehlgeschlagen');
      const data = await response.json();
      setNewMessage(data.improved_text);
      toast.success('✨ Text verbessert – bitte prüfen, dann senden', {
        duration: 8000,
        action: { label: 'Rückgängig', onClick: () => setNewMessage(original) },
      });
    } catch (e) {
      toast.error('KI-Korrektur momentan nicht verfügbar');
    } finally {
      setImproving(false);
    }
  };

  const fileInputRef = useRef(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [pendingMediaFile, setPendingMediaFile] = useState(null); // { file, url, kind, name }
  const [mediaCaption, setMediaCaption] = useState("");

  const handleMediaSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedChat) return;
    if (file.size > 16 * 1024 * 1024) {
      toast.error("Datei zu gross (max. 16 MB)");
      return;
    }
    const mt = file.type || "";
    const kind = mt.startsWith("image/") ? "image" : mt.startsWith("video/") ? "video" : "document";
    setMediaCaption("");
    setPendingMediaFile({ file, url: URL.createObjectURL(file), kind, name: file.name });
  };

  const discardPendingMediaFile = () => {
    if (pendingMediaFile?.url) URL.revokeObjectURL(pendingMediaFile.url);
    setPendingMediaFile(null);
    setMediaCaption("");
  };

  const sendPendingMediaFile = async () => {
    if (!pendingMediaFile || !selectedChat) return;
    setUploadingMedia(true);
    shouldAutoScrollRef.current = true;
    try {
      const authToken = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", pendingMediaFile.file);
      fd.append("phone_number", selectedChat.chat_id);
      fd.append("caption", mediaCaption || "");
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/send-media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.status === "sent") {
        discardPendingMediaFile();
        fetchMessages(selectedChat.chat_id);
        fetchBotStatus(selectedChat.chat_id);
        toast.success("Datei gesendet");
      } else if (response.ok && result.status === "preview_blocked") {
        discardPendingMediaFile();
        fetchMessages(selectedChat.chat_id);
        toast.info("Vorschau: Datei gespeichert & angezeigt – echter Versand ist im Test-System deaktiviert.");
      } else if (response.ok && result.status === "outbound_disabled") {
        toast.error("WhatsApp-Versand ist ausgeschaltet – Datei wurde nicht gesendet.");
      } else {
        toast.error(result.error || result.detail || "Fehler beim Senden der Datei");
      }
    } catch (error) {
      toast.error("Fehler beim Senden der Datei");
      console.error("Error sending media:", error);
    } finally {
      setUploadingMedia(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;
    
    setSending(true);
    shouldAutoScrollRef.current = true;

    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          phone_number: selectedChat.chat_id,
          message: newMessage,
          preview_url: false
        })
      });
      
      if (response.ok) {
        setNewMessage("");
        fetchMessages(selectedChat.chat_id);
        fetchBotStatus(selectedChat.chat_id);
        toast.success("Nachricht gesendet");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Senden");
      }
    } catch (error) {
      toast.error("Fehler beim Senden der Nachricht");
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  const getMessagePreview = (message) => {
    if (!message) return "";
    const rawContent = message.content ?? message.body;
    if (typeof rawContent === "string" && rawContent.trim()) {
      return rawContent;
    }
    if (typeof rawContent === "number") {
      return String(rawContent);
    }
    if (rawContent && typeof rawContent === "object") {
      try {
        return JSON.stringify(rawContent);
      } catch (error) {
        return String(rawContent);
      }
    }
    const msgType = message.message_type || message.type;
    if (message.template_name) return `[Template: ${message.template_name}]`;
    if (msgType) return `[${msgType}]`;
    return "[Nachricht]";
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    } else {
      return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) + " " +
             date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    }
  };

  const formatTokenExpiry = (iso) => {
    if (!iso) return { label: "Unbegrenzt (System-Token)", expired: false, soon: false };
    const d = new Date(iso);
    if (isNaN(d.getTime())) return { label: "—", expired: false, soon: false };
    const ms = d.getTime() - Date.now();
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    const dateStr = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    const label = days >= 0
      ? `${dateStr} (in ${days} Tag${days === 1 ? "" : "en"})`
      : `${dateStr} (abgelaufen vor ${Math.abs(days)} Tag${Math.abs(days) === 1 ? "" : "en"})`;
    return { label, expired: ms < 0, soon: ms >= 0 && days <= 7 };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "read":
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
      case "delivered":
        return <CheckCheck className="h-3 w-3 text-gray-400" />;
      case "sent":
        return <Check className="h-3 w-3 text-gray-400" />;
      default:
        return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };

  if (!whatsappStatus.configured) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">WhatsApp Chat</h1>
            <p className="text-gray-500 mt-1">Kommunizieren Sie mit Ihren Kunden über WhatsApp</p>
          </div>
        </div>

        <Card className="max-w-2xl mx-auto mt-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-6 w-6" />
              WhatsApp Business API einrichten
            </CardTitle>
            <CardDescription>
              Verbinden Sie Ihr WhatsApp Business Konto in wenigen Klicks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert className="bg-green-50 border-green-200">
              <Zap className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Einfache Einrichtung:</strong> Wie bei Superchat - mit Meta verbinden in 3 Schritten
              </AlertDescription>
            </Alert>

            <div className="p-6 bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg border-2 border-teal-200">
              <h3 className="font-semibold text-lg mb-2">Mit WhatsApp verbinden</h3>
              <p className="text-sm text-gray-600 mb-4">
                Klicken Sie auf den Button. Wenn noch nicht konfiguriert, führen wir Sie durch die Einrichtung.
              </p>
              
              <Button
                onClick={handleConnectClick}
                disabled={connecting}
                size="lg"
                className="w-full bg-[#25D366] hover:bg-[#1fb855] text-white"
              >
                {connecting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verbindung läuft...
                  </>
                ) : (
                  <>
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Mit WhatsApp Business verbinden
                  </>
                )}
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <Separator className="flex-1" />
              <span className="text-sm text-gray-500">oder</span>
              <Separator className="flex-1" />
            </div>

            <details className="border rounded-lg p-4">
              <summary className="cursor-pointer font-medium text-sm">
                Manuelle Konfiguration (für Fortgeschrittene)
              </summary>
              <div className="mt-4 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2 text-sm">Alle benötigten Informationen:</h3>
                  <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                    <li>Meta App-ID & App-Secret (von Developer Portal → Einstellungen → Allgemein)</li>
                    <li>Phone Number ID & Access Token (von Developer Portal → WhatsApp → API-Setup)</li>
                    <li>WhatsApp Business Account ID</li>
                  </ul>
                </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 my-3">
              <p className="text-sm font-semibold text-purple-900 mb-1">Meta App Credentials</p>
              <p className="text-xs text-purple-700">Von Meta Developer Portal → Einstellungen → Allgemein</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup_meta_app_id">Meta App-ID</Label>
              <Input
                id="setup_meta_app_id"
                placeholder="z.B. 4144575499089504"
                value={settings.meta_app_id || ""}
                onChange={(e) => setSettings({ ...settings, meta_app_id: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup_meta_app_secret">Meta App-Secret</Label>
              <Input
                id="setup_meta_app_secret"
                type="password"
                placeholder="Ihr App-Geheimnis"
                value={settings.meta_app_secret || ""}
                onChange={(e) => setSettings({ ...settings, meta_app_secret: e.target.value })}
              />
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 my-3">
              <p className="text-sm font-semibold text-teal-900 mb-1">WhatsApp API Credentials</p>
              <p className="text-xs text-teal-700">Von Meta Developer Portal → WhatsApp → API-Setup</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_id">Telefonnummer-ID</Label>
              <Input
                id="phone_id"
                placeholder="z.B. 380328448505955"
                value={settings.phone_id}
                onChange={(e) => setSettings({ ...settings, phone_id: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="access_token">Zugriffstoken</Label>
              <Input
                id="access_token"
                type="password"
                placeholder="EAA..."
                value={settings.access_token}
                onChange={(e) => setSettings({ ...settings, access_token: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="verify_token">Webhook Verify Token</Label>
              <Input
                id="verify_token"
                placeholder="strongfit_webhook_2025"
                value={settings.verify_token}
                onChange={(e) => setSettings({ ...settings, verify_token: e.target.value })}
              />
              <p className="text-xs text-gray-500">
                Dieser Token wird zur Verifizierung des Webhooks verwendet
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_account_id">Business Account ID (optional)</Label>
              <Input
                id="business_account_id"
                placeholder="z.B. 123456789012345"
                value={settings.business_account_id}
                onChange={(e) => setSettings({ ...settings, business_account_id: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="enabled">WhatsApp aktivieren</Label>
                <p className="text-sm text-gray-500">Integration einschalten</p>
              </div>
              <Switch
                id="enabled"
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="ai_auto_reply">KI-Autoantwort</Label>
                <p className="text-sm text-gray-500">Automatische Antworten mit KI</p>
              </div>
              <Switch
                id="ai_auto_reply"
                checked={settings.ai_auto_reply_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, ai_auto_reply_enabled: checked })}
              />
            </div>

                <Button onClick={saveSettings} className="w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Einstellungen speichern
                </Button>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="text-sm text-yellow-800">
                    <strong>Wichtig:</strong> Nach dem Speichern müssen Sie die Webhook-URL in Ihrem Meta-Dashboard konfigurieren:
                    <br />
                    <code className="bg-white px-2 py-1 rounded mt-2 block text-xs">
                      {window.location.origin}/api/whatsapp/webhook
                    </code>
                  </div>
                </div>
              </div>
            </details>

            <div className="bg-gray-50 border rounded-lg p-4 mt-6">
              <h3 className="font-semibold text-sm mb-2">Webhook-URL für Meta:</h3>
              <code className="bg-white px-3 py-2 rounded block text-xs break-all">
                {window.location.origin}/api/whatsapp/webhook
              </code>
              <p className="text-xs text-gray-600 mt-2">
                Verify Token: <code className="bg-white px-2 py-0.5 rounded">strongfit_webhook_2025</code>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Setup Wizard Dialog */}
        <Dialog open={setupWizardOpen} onOpenChange={setSetupWizardOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-teal-600" />
                WhatsApp einrichten - Schritt {setupStep} von 3
              </DialogTitle>
              <DialogDescription>
                Folgen Sie den Schritten, um WhatsApp zu verbinden
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {setupStep === 1 && (
                <div className="space-y-4">
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-blue-800">
                      <strong>Schritt 1:</strong> Meta Developer App erstellen
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                      Um WhatsApp zu nutzen, benötigen Sie eine Meta Developer App. Das dauert nur ~5 Minuten:
                    </p>

                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      <li>
                        Öffnen Sie:{" "}
                        <a 
                          href="https://developers.facebook.com/apps/create/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline font-medium"
                        >
                          Meta Developer Portal →
                        </a>
                      </li>
                      <li>Melden Sie sich mit Ihrem Facebook/Meta-Konto an</li>
                      <li>Klicken Sie auf "App erstellen"</li>
                      <li>Wählen Sie App-Typ: <strong>"Business"</strong></li>
                      <li>App-Name: z.B. "StrongFit WhatsApp"</li>
                      <li>Geben Sie Ihre Geschäfts-E-Mail ein</li>
                      <li>Klicken Sie auf "App erstellen"</li>
                      <li>Fügen Sie das Produkt <strong>"WhatsApp"</strong> hinzu</li>
                    </ol>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                      <p className="text-xs text-yellow-800">
                        <strong>Tipp:</strong> Sie brauchen ein Meta Business Portfolio. Falls Sie keins haben, erstellt Meta automatisch eins für Sie.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setSetupWizardOpen(false)}
                      className="flex-1"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={() => setSetupStep(2)}
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                    >
                      Weiter - App erstellt
                    </Button>
                  </div>
                </div>
              )}

              {setupStep === 2 && (
                <div className="space-y-4">
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-blue-800">
                      <strong>Schritt 2:</strong> App-Credentials kopieren
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                      Jetzt kopieren Sie die App-ID und das App-Secret:
                    </p>

                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      <li>
                        Gehen Sie zu:{" "}
                        <a 
                          href="https://developers.facebook.com/apps/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline font-medium"
                        >
                          Meine Apps →
                        </a>
                      </li>
                      <li>Wählen Sie Ihre erstellte App aus</li>
                      <li>Gehen Sie zu "Einstellungen" → "Allgemein" (linke Sidebar)</li>
                      <li>Kopieren Sie die <strong>App-ID</strong></li>
                      <li>Klicken Sie bei "App-Geheimnis" auf <strong>"Anzeigen"</strong> und kopieren Sie es</li>
                    </ol>

                    <div className="space-y-3 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="app_id">App-ID</Label>
                        <Input
                          id="app_id"
                          placeholder="z.B. 1234567890123456"
                          value={tempAppId}
                          onChange={(e) => setTempAppId(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="app_secret">App-Secret (Geheimnis)</Label>
                        <Input
                          id="app_secret"
                          type="password"
                          placeholder="Ihr App-Secret hier einfügen"
                          value={tempAppSecret}
                          onChange={(e) => setTempAppSecret(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setSetupStep(1)}
                      className="flex-1"
                    >
                      Zurück
                    </Button>
                    <Button
                      onClick={saveMetaCredentials}
                      disabled={!tempAppId.trim() || !tempAppSecret.trim()}
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                    >
                      Speichern & Weiter
                    </Button>
                  </div>
                </div>
              )}

              {setupStep === 3 && (
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <AlertDescription className="text-green-800">
                      <strong>✅ Schritt 3:</strong> Bereit zum Verbinden!
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                      Perfekt! Ihre Meta App ist jetzt konfiguriert. Im nächsten Schritt:
                    </p>

                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      <li>Ein Meta-Popup öffnet sich</li>
                      <li>Melden Sie sich mit Ihrem Meta-Konto an</li>
                      <li>Wählen Sie Ihre WhatsApp Business Nummer aus (oder fügen Sie eine neue hinzu)</li>
                      <li>Erteilen Sie die erforderlichen Berechtigungen</li>
                      <li>✅ Fertig! Sie können sofort loslegen</li>
                    </ol>

                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-teal-800 font-medium">
                        Nach der Verbindung können Sie direkt Nachrichten senden und empfangen!
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4" data-testid="whatsapp-setup-actions">
                    <Button
                      variant="outline"
                      onClick={() => setSetupWizardOpen(false)}
                      className="flex-1"
                      data-testid="whatsapp-setup-later-button"
                    >
                      Später
                    </Button>
                    <Button
                      onClick={completeSetup}
                      className="flex-1 bg-[#25D366] hover:bg-[#1fb855]"
                      data-testid="whatsapp-setup-connect-button"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Jetzt verbinden
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col overflow-hidden -m-4 lg:-m-8">
      {/* Header - nur Status und Aktionen, kein doppelter Titel */}
      <div className="flex items-center justify-between p-3 border-b bg-white shrink-0" data-testid="whatsapp-page-header">
        <div className="flex items-center gap-2" data-testid="whatsapp-page-status">
          {whatsappStatus.enabled ? (
            (() => {
              const tokenExpiry = formatTokenExpiry(settings.token_expires_at);
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="whatsapp-connection-status-trigger"
                      className="inline-flex items-center gap-2 focus:outline-none"
                    >
                      <Badge variant="success" data-testid="whatsapp-status-connected">
                        <MessageCircle className="h-3 w-3 mr-1" />
                        Verbunden
                      </Badge>
                      {settings.display_phone_number && (
                        <span className="text-sm font-medium text-gray-700 hidden sm:inline" data-testid="whatsapp-connected-number">
                          {settings.display_phone_number}
                        </span>
                      )}
                      <Info className="h-3.5 w-3.5 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80" data-testid="whatsapp-connection-details">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <MessageCircle className="h-4 w-4 text-green-600" />
                        <span className="font-semibold text-sm">WhatsApp-Verbindung</span>
                        {settings.setup_method === "embedded_signup" && (
                          <Badge variant="secondary" className="ml-auto text-[10px]">Meta Signup</Badge>
                        )}
                      </div>
                      <div className="flex items-start gap-2" data-testid="whatsapp-detail-number">
                        <Phone className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Nummer</p>
                          <p className="text-sm font-medium">{settings.display_phone_number || settings.phone_id || "—"}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2" data-testid="whatsapp-detail-waba">
                        <Hash className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">WhatsApp Business Account (WABA)</p>
                          <p className="text-sm font-medium break-all">{settings.business_account_id || "—"}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2" data-testid="whatsapp-detail-token-expiry">
                        <CalendarClock className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500">Token gültig bis</p>
                          <p className={`text-sm font-medium ${tokenExpiry.expired ? "text-red-600" : tokenExpiry.soon ? "text-amber-600" : ""}`}>
                            {tokenExpiry.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })()
          ) : (
            <Badge variant="secondary" data-testid="whatsapp-status-disconnected">Nicht konfiguriert</Badge>
          )}
        </div>
        
        <div className="flex gap-2" data-testid="whatsapp-header-actions">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/whatsapp/templates')}
            data-testid="whatsapp-header-templates-button"
          >
            <FileText className="h-4 w-4 mr-2" />
            Templates
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/whatsapp/logs')}
            className="text-blue-600"
            data-testid="whatsapp-header-webhook-logs-button"
          >
            <FileText className="h-4 w-4 mr-2" />
            Webhook Logs
          </Button>

          <Dialog open={onbInviteOpen} onOpenChange={setOnbInviteOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="whatsapp-new-onboarding-chat-button"
                onClick={() => setOnbInvite(i => ({ ...i, phone: selectedChat?.chat_id || i.phone }))}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Neuer Chat (Onboarding)
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="whatsapp-onboarding-invite-dialog">
              <DialogHeader>
                <DialogTitle>Onboarding-Einladung senden</DialogTitle>
                <DialogDescription>
                  Startet einen Chat mit der Nummer und sendet die Onboarding-Vorlage mit persönlichem Link.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Telefonnummer *</Label>
                  <Input value={onbInvite.phone} onChange={e => setOnbInvite({ ...onbInvite, phone: e.target.value })} placeholder="+41 79 …" data-testid="onb-invite-phone" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Studio</Label>
                    <Input value={onbInvite.studio} onChange={e => setOnbInvite({ ...onbInvite, studio: e.target.value })} placeholder="StrongFit Wetzikon" />
                  </div>
                  <div>
                    <Label>Absender</Label>
                    <Input value={onbInvite.sender} onChange={e => setOnbInvite({ ...onbInvite, sender: e.target.value })} placeholder="dein Name" />
                  </div>
                </div>
                <div className="rounded-md border p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!onbInvite.personalize}
                      onChange={e => setOnbInvite({ ...onbInvite, personalize: e.target.checked })}
                      data-testid="onb-invite-personalize-toggle"
                    />
                    Mit Namen personalisieren („Hey Alex 👋")
                  </label>
                  {onbInvite.personalize && (
                    <div>
                      <Label>Name des Kunden</Label>
                      <Input value={onbInvite.name} onChange={e => setOnbInvite({ ...onbInvite, name: e.target.value })} placeholder="z.B. Alex" data-testid="onb-invite-name" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {onbInvite.personalize && onbInvite.name.trim()
                    ? 'Nutzt die Meta-Vorlage „onboarding_einladung_name_v1" (Begrüssung „Hey ' + onbInvite.name.trim() + ' 👋").'
                    : 'Nutzt die Meta-Vorlage „onboarding_einladung_v2" (Begrüssung „Hey 👋" ohne Namen).'} Hinweis: Die Vorlage muss in Meta genehmigt sein, damit der Versand funktioniert.
                </p>
                <Button
                  className="w-full"
                  disabled={onbSending || !onbInvite.phone.trim()}
                  data-testid="onb-invite-submit"
                  onClick={async () => {
                    setOnbSending(true);
                    try {
                      const tk = localStorage.getItem('token');
                      const res = await fetch(`${BACKEND_URL}/api/whatsapp/send-onboarding-invite`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
                        body: JSON.stringify({
                          recipient_phone: onbInvite.phone,
                          customer_name: onbInvite.name,
                          studio_name: onbInvite.studio,
                          sender_name: onbInvite.sender,
                          personalize: !!onbInvite.personalize,
                        }),
                      });
                      const data = await res.json();
                      if (!res.ok) throw new Error(data?.detail || 'Fehler');
                      toast.success('Onboarding-Einladung gesendet');
                      setOnbInviteOpen(false);
                      setOnbInvite({ phone: '', name: '', studio: '', sender: '', personalize: false });
                      if (typeof fetchChats === 'function') fetchChats();
                    } catch (e) {
                      toast.error(e.message || 'Senden fehlgeschlagen');
                    } finally {
                      setOnbSending(false);
                    }
                  }}
                >
                  {onbSending ? 'Sendet…' : 'Einladung senden'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={sendTemplateOpen} onOpenChange={setSendTemplateOpen}>
            <DialogTrigger asChild>
              <Button
                variant="default"
                size="sm"
                onClick={fetchTemplates}
                data-testid="whatsapp-header-send-template-button"
              >
                <Send className="h-4 w-4 mr-2" />
                Template senden
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]" data-testid="whatsapp-send-template-dialog">
              <DialogHeader>
                <DialogTitle data-testid="whatsapp-send-template-title">Template-Nachricht senden</DialogTitle>
                <DialogDescription data-testid="whatsapp-send-template-description">
                  Senden Sie eine genehmigte Template-Nachricht an einen Empfänger
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="template_select">Template auswählen</Label>
                  <select
                    id="template_select"
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="whatsapp-template-select"
                  >
                    <option value="">-- Bitte wählen --</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.name}>
                        {template.name} - {template.language} ({template.category})
                      </option>
                    ))}
                  </select>
                  {templates.length === 0 && (
                    <p className="text-xs text-gray-500" data-testid="whatsapp-template-empty">
                      Keine genehmigten Templates verfügbar. Bitte erstellen Sie zuerst ein Template.
                    </p>
                  )}
                </div>
                
                {/* Template Parameter Eingabefelder */}
                {templateParameters.length > 0 && (() => {
                  const isOnb = selectedTemplateObj && (((selectedTemplateObj.name || '').toLowerCase().includes('onboarding')) ||
                    (selectedTemplateObj.components || []).some(c => c.type === 'BUTTONS' && (c.buttons || []).some(b => (b.url || '').includes('/onboarding/'))));
                  if (isOnb) {
                    return (
                      <div className="space-y-2 p-3 bg-blue-50 rounded-lg" data-testid="whatsapp-onboarding-params">
                        <Label className="text-sm font-medium text-blue-900">Onboarding-Einladung</Label>
                        <div className="text-xs text-blue-700 p-2 bg-white rounded">
                          {selectedTemplateObj?.components?.find(c => c.type === 'BODY')?.text}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-blue-800">Name des Kunden (optional)</Label>
                          <Input
                            placeholder="z.B. Alex"
                            value={templateParameters[0] || ''}
                            onChange={(e) => {
                              const np = [...templateParameters];
                              np[0] = e.target.value;
                              setTemplateParameters(np);
                            }}
                            className="border-blue-300"
                            data-testid="whatsapp-onboarding-name"
                          />
                        </div>
                        <p className="text-xs text-blue-700">
                          ✅ Onboarding-Link, Studio und dein Absendername werden automatisch eingefügt.
                        </p>
                      </div>
                    );
                  }
                  return (
                  <div className="space-y-2 p-3 bg-blue-50 rounded-lg" data-testid="whatsapp-template-parameters">
                    <Label className="text-sm font-medium text-blue-900">
                      Template-Variablen ausfüllen
                    </Label>
                    {selectedTemplateObj && selectedTemplateObj.components && (
                      <div className="text-xs text-blue-700 mb-2 p-2 bg-white rounded" data-testid="whatsapp-template-body-preview">
                        {selectedTemplateObj.components.find(c => c.type === 'BODY')?.text}
                      </div>
                    )}
                    {templateParameters.map((param, index) => (
                      <div key={index} className="space-y-1">
                        <Label className="text-xs text-blue-800">
                          Variable {`{{${index + 1}}}`}
                        </Label>
                        <Input
                          placeholder={`Wert für {{${index + 1}}}`}
                          value={param}
                          onChange={(e) => {
                            const newParams = [...templateParameters];
                            newParams[index] = e.target.value;
                            setTemplateParameters(newParams);
                          }}
                          className="border-blue-300"
                          data-testid={`whatsapp-template-param-${index}`}
                        />
                      </div>
                    ))}
                  </div>
                  );
                })()}

                <div className="space-y-2">
                  <Label htmlFor="customer_search">Kunde suchen</Label>
                  <div className="relative">
                    <Input
                      id="customer_search"
                      type="text"
                      placeholder="Name, Email oder Telefon..."
                      value={customerSearch}
                      onChange={(e) => {
                        setCustomerSearch(e.target.value);
                      }}
                      className="pr-10"
                      data-testid="whatsapp-customer-search-input"
                    />
                    <User className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    
                    {/* Search Results Dropdown */}
                    {customerResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="whatsapp-customer-search-results">
                        {customerResults.map((customer) => (
                          <div
                            key={customer.id}
                            onClick={() => selectCustomer(customer)}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            data-testid={`whatsapp-customer-search-item-${customer.id}`}
                          >
                            <div className="font-medium text-gray-900">
                              {customer.first_name} {customer.last_name}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                              {customer.email && <span>📧 {customer.email}</span>}
                              {customer.phone && <span>📱 {customer.phone}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recipient_phone">Telefonnummer</Label>
                  <Input
                    id="recipient_phone"
                    type="tel"
                    placeholder="+41791234567"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    disabled={selectedCustomer !== null}
                    data-testid="whatsapp-recipient-phone-input"
                  />
                  <p className="text-xs text-gray-500" data-testid="whatsapp-recipient-phone-helper">
                    {selectedCustomer ? "Aus Kundenprofil übernommen" : "Im Format: +41791234567 (mit Ländercode)"}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2" data-testid="whatsapp-template-dialog-actions">
                <Button
                  variant="outline"
                  onClick={() => setSendTemplateOpen(false)}
                  data-testid="whatsapp-template-dialog-cancel"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={sendTemplateMessage}
                  disabled={sending || !selectedTemplate || !recipientPhone}
                  data-testid="whatsapp-template-dialog-send"
                >
                  {sending ? "Sende..." : "Nachricht senden"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="whatsapp-header-settings-button">
                <Settings className="h-4 w-4 mr-2" />
                Einstellungen
              </Button>
            </DialogTrigger>

          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>WhatsApp-Einstellungen</DialogTitle>
              <DialogDescription>
                Konfigurieren Sie Ihre WhatsApp Business API
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-semibold text-blue-900 mb-1">Meta App Credentials</p>
                <p className="text-xs text-blue-700">Von Meta Developer Portal → Einstellungen → Allgemein</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="settings_meta_app_id">Meta App-ID</Label>
                <Input
                  id="settings_meta_app_id"
                  placeholder="z.B. 4144575499089504"
                  value={settings.meta_app_id || ""}
                  onChange={(e) => setSettings({ ...settings, meta_app_id: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="settings_meta_app_secret">Meta App-Secret</Label>
                <Input
                  id="settings_meta_app_secret"
                  type="password"
                  placeholder="Ihr App-Geheimnis von Meta"
                  value={settings.meta_app_secret || ""}
                  onChange={(e) => setSettings({ ...settings, meta_app_secret: e.target.value })}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 my-4">
                <p className="text-sm font-semibold text-blue-900 mb-1">WhatsApp API Credentials</p>
                <p className="text-xs text-blue-700">Von Meta Developer Portal → WhatsApp → API-Setup</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="settings_phone_id">Phone Number ID</Label>
                <Input
                  id="settings_phone_id"
                  placeholder="z.B. 380328448505955"
                  value={settings.phone_id === "***SET***" ? "" : settings.phone_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSettings({ ...settings, phone_id: value });
                    setActualCredentials({ ...actualCredentials, phone_id: value });
                  }}
                />
                {settings.phone_id === "***SET***" && (
                  <p className="text-xs text-green-600">✓ Bereits gespeichert (Neu eingeben zum Ändern)</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="settings_access_token">Access Token</Label>
                <Input
                  id="settings_access_token"
                  type="text"
                  placeholder="EAA..."
                  value={settings.access_token === "***SET***" ? "" : settings.access_token}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSettings({ ...settings, access_token: value });
                    setActualCredentials({ ...actualCredentials, access_token: value });
                  }}
                />
                {settings.access_token === "***SET***" && (
                  <p className="text-xs text-green-600">✓ Bereits gespeichert (Neu eingeben zum Ändern)</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="settings_business_account_id">Business Account ID (WABA)</Label>
                <Input
                  id="settings_business_account_id"
                  placeholder="z.B. 344124728795281"
                  value={settings.business_account_id || ""}
                  onChange={(e) => setSettings({ ...settings, business_account_id: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label>WhatsApp aktivieren</Label>
                  <p className="text-xs text-gray-500">Integration einschalten</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>

              <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${settings.outbound_enabled === false ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`} data-testid="whatsapp-outbound-toggle">
                <div>
                  <Label>Ausgehender Versand</Label>
                  <p className="text-xs text-gray-600">
                    {settings.outbound_enabled === false
                      ? 'AUS – es werden KEINE WhatsApps an Kunden gesendet (z.B. während Tests / Backup-Import).'
                      : 'AN – WhatsApp-Nachrichten an Kunden werden normal versendet.'}
                  </p>
                </div>
                <Switch
                  checked={settings.outbound_enabled !== false}
                  onCheckedChange={toggleOutbound}
                  data-testid="whatsapp-outbound-switch"
                />
              </div>

              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <Label className="text-purple-900">KI-Autoantwort</Label>
                <p className="text-xs text-purple-700 mt-1">
                  Wird zentral gesteuert: <strong>Einstellungen → KI &amp; API → KI-Chatbot</strong> (global)
                  bzw. „Bot pausieren" direkt im jeweiligen Chat.
                </p>
              </div>

              <Button onClick={saveSettings} className="w-full">
                Speichern
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat List */}
        <div className="w-80 border-r bg-white flex flex-col">
          {/* Tabs für Status-Filter */}
          <div className="border-b bg-gray-50">
            <div className="flex">
              <button
                data-testid="whatsapp-tab-active"
                onClick={() => setChatStatus("active")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  chatStatus === "active"
                    ? "bg-white text-teal-600 border-b-2 border-teal-600"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  <span>Aktiv</span>
                </div>
              </button>
              <button
                data-testid="whatsapp-tab-archived"
                onClick={() => setChatStatus("archived")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  chatStatus === "archived"
                    ? "bg-white text-teal-600 border-b-2 border-teal-600"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <CheckCheck className="h-4 w-4" />
                  <span>Erledigt</span>
                </div>
              </button>
              <button
                data-testid="whatsapp-tab-deleted"
                onClick={() => setChatStatus("deleted")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  chatStatus === "deleted"
                    ? "bg-white text-teal-600 border-b-2 border-teal-600"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  <span>Gelöscht</span>
                </div>
              </button>
            </div>
          </div>
          
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900" data-testid="whatsapp-chat-list-title">
              {chatStatus === "active" && "Aktive Konversationen"}
              {chatStatus === "archived" && "Erledigte Chats"}
              {chatStatus === "deleted" && "Papierkorb"}
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-testid="whatsapp-chat-list-count">{chats.length} Chats</p>
          </div>
          
          <ScrollArea className="flex-1" data-testid="whatsapp-chat-list">
            {loading ? (
              <div className="p-8 text-center" data-testid="whatsapp-chat-list-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Lade Chats...</p>
              </div>
            ) : chats.length === 0 ? (
              <div className="p-8 text-center text-gray-500" data-testid="whatsapp-chat-list-empty">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Keine Nachrichten</p>
              </div>
            ) : (
              <div className="divide-y" data-testid="whatsapp-chat-list-items">
                {chats.map((chat) => {
                  const isSelected = selectedChat?.chat_id === chat._id || selectedChat?.chat_id === chat.phone;
                  const chatId = chat.phone || chat._id;
                  const previewText = getMessagePreview(chat.last_message);
                  
                  return (
                    <button
                      key={chat._id}
                      type="button"
                      data-testid={`whatsapp-chat-item-${chat._id}`}
                      onClick={() => {
                        setSelectedChat({ chat_id: chatId, ...chat.last_message });
                        handleChatSelect(chatId);
                      }}
                      className={`w-full text-left p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        isSelected ? "bg-teal-50 border-l-4 border-teal-500" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm truncate" data-testid={`whatsapp-chat-item-name-${chat._id}`}>
                              {chat.customer_name || chat.last_message?.customer_name || chat.phone || chat.last_message?.from_phone}
                            </span>
                            <span className="text-xs text-gray-500" data-testid={`whatsapp-chat-item-time-${chat._id}`}>
                              {formatTime(chat.last_message?.timestamp)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 truncate flex-1" data-testid={`whatsapp-chat-item-preview-${chat._id}`}>
                              {previewText}
                            </span>
                            {chat.unread_count > 0 && (
                              <Badge
                                variant="default"
                                className="bg-teal-500 h-5 min-w-5 px-1.5 flex items-center justify-center pointer-events-none"
                                data-testid={`whatsapp-chat-item-unread-${chat._id}`}
                              >
                                {chat.unread_count}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Messages */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className="bg-white p-4 border-b flex items-center gap-3" data-testid="whatsapp-chat-header">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold" data-testid="whatsapp-chat-header-name">
                    {selectedChat.customer_name || selectedChat.phone || selectedChat.from_phone}
                  </h2>
                  <p className="text-xs text-gray-500 flex items-center gap-1" data-testid="whatsapp-chat-header-phone">
                    <Phone className="h-3 w-3" />
                    {selectedChat.from_phone || selectedChat.chat_id}
                  </p>
                  {serviceWindow && (
                    serviceWindow.open ? (
                      <p className="text-xs mt-0.5" data-testid="whatsapp-service-window-open">
                        <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">
                          🟢 24h-Fenster offen – noch {Math.floor(serviceWindow.minutes_left / 60)}h {serviceWindow.minutes_left % 60}m (freie Nachricht möglich)
                        </span>
                      </p>
                    ) : (
                      <p className="text-xs mt-0.5" data-testid="whatsapp-service-window-closed">
                        <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">
                          🔴 24h-Fenster geschlossen – nur Template möglich
                        </span>
                      </p>
                    )
                  )}
                  {adminTakeoverUntil && new Date(adminTakeoverUntil) > new Date() && (
                    <p className="text-xs text-amber-600 mt-0.5" data-testid="whatsapp-admin-takeover-hint">
                      🤝 Du hast übernommen – Bot antwortet nur bei neuen Themen (bis {new Date(adminTakeoverUntil).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })})
                    </p>
                  )}
                  {!botEnabled && (
                    <p className="text-xs mt-0.5" data-testid="whatsapp-bot-paused-badge">
                      <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">
                        🤫 {botPausedInfo?.paused_by === 'customer'
                          ? `Vom Kunden pausiert${botPausedInfo?.paused_until ? ` bis ${new Date(botPausedInfo.paused_until).toLocaleString('de-CH', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}`
                          : 'Bot pausiert (von dir)'}
                      </span>
                    </p>
                  )}
                </div>
                <div className="flex gap-2" data-testid="whatsapp-chat-header-actions">
                  <Button
                    onClick={toggleBotStatus}
                    variant="outline"
                    size="sm"
                    disabled={botStatusLoading}
                    data-testid="whatsapp-chatbot-toggle-button"
                  >
                    {botEnabled ? "Bot pausieren" : "Bot aktivieren"}
                  </Button>
                  {/* Buttons abhängig vom Status */}
                  {chatStatus === "active" && (
                    <Button
                      onClick={() => archiveChat(selectedChat.chat_id || selectedChat.from_phone)}
                      variant="outline"
                      size="sm"
                      className="text-gray-600"
                      data-testid="whatsapp-chat-mark-done-button"
                    >
                      <CheckCheck className="h-4 w-4 mr-1" />
                      Als erledigt markieren
                    </Button>
                  )}
                  
                  {chatStatus === "archived" && (
                    <>
                      <Button
                        onClick={() => restoreChat(selectedChat.chat_id || selectedChat.from_phone, false)}
                        variant="outline"
                        size="sm"
                        className="text-teal-600"
                        data-testid="whatsapp-chat-restore-button"
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Reaktivieren
                      </Button>
                      <Button
                        onClick={() => deleteChat(selectedChat.chat_id || selectedChat.from_phone)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        data-testid="whatsapp-chat-delete-button"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Löschen
                      </Button>
                    </>
                  )}
                  
                  {chatStatus === "deleted" && (
                    <Button
                      onClick={() => restoreChat(selectedChat.chat_id || selectedChat.from_phone, true)}
                      variant="outline"
                      size="sm"
                      className="text-teal-600"
                      data-testid="whatsapp-chat-restore-deleted-button"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Wiederherstellen
                    </Button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="relative flex-1 min-h-0" data-testid="whatsapp-message-list-wrapper">
                <ScrollArea className="h-full min-h-0 p-4" ref={scrollAreaRef} data-testid="whatsapp-message-list">
                  <div className="space-y-4 max-w-4xl mx-auto">
                    {messages.map((msg, idx) => {
                    const messageKey = msg.id || msg.message_id || `${msg.timestamp}-${idx}`;
                    const messageTestId = `whatsapp-message-${idx}`;
                    // Bestimme, ob die Nachricht vom Kunden ist
                    const isFromCustomer = msg.is_from_customer !== undefined 
                      ? msg.is_from_customer 
                      : msg.direction === 'incoming';
                    
                    // Datum-Trenner prüfen
                    const currentDate = new Date(msg.timestamp).toDateString();
                    const previousDate = idx > 0 ? new Date(messages[idx - 1].timestamp).toDateString() : null;
                    const showDateSeparator = currentDate !== previousDate;
                    
                    // Datum-Label generieren
                    const getDateLabel = (timestamp) => {
                      const msgDate = new Date(timestamp);
                      const today = new Date();
                      const yesterday = new Date(today);
                      yesterday.setDate(yesterday.getDate() - 1);
                      
                      // Berechne Wochenbeginn (Montag)
                      const weekStart = new Date(today);
                      const dayOfWeek = today.getDay();
                      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sonntag = 0, also 6 Tage zurück
                      weekStart.setDate(today.getDate() - diff);
                      weekStart.setHours(0, 0, 0, 0);
                      
                      if (msgDate.toDateString() === today.toDateString()) {
                        return "Heute";
                      } else if (msgDate.toDateString() === yesterday.toDateString()) {
                        return "Gestern";
                      } else if (msgDate >= weekStart) {
                        // Diese Woche - zeige Wochentag
                        return msgDate.toLocaleDateString('de-CH', { weekday: 'long' });
                      } else {
                        // Älter - zeige Datum
                        return msgDate.toLocaleDateString('de-CH', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        });
                      }
                    };
                    
                    const hasText = !!(msg.content || msg.body);
                    const actionsMenu = (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="self-center opacity-40 hover:opacity-100 transition-opacity p-1 text-gray-400 shrink-0"
                            data-testid={`${messageTestId}-actions-trigger`}
                            aria-label="Nachricht-Aktionen"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align={isFromCustomer ? "start" : "end"} className="w-40">
                          {hasText && (
                            <DropdownMenuItem onClick={() => startEditMessage(msg)} data-testid={`${messageTestId}-edit`}>
                              <Pencil className="w-4 h-4 mr-2" /> Bearbeiten
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => deleteMessage(msg)}
                            className="text-red-600 focus:text-red-600"
                            data-testid={`${messageTestId}-delete`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );

                    return (
                      <div key={messageKey} className="space-y-2" data-testid={`${messageTestId}-block`}>
                        {showDateSeparator && (
                          <div className="flex items-center justify-center my-4" data-testid={`${messageTestId}-date-separator`}>
                            <div className="px-4 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                              {getDateLabel(msg.timestamp)}
                            </div>
                          </div>
                        )}
                        <div
                          className={`flex items-center gap-1 group ${isFromCustomer ? "justify-start" : "justify-end"}`}
                          data-testid={`${messageTestId}-row`}
                        >
                          {!isFromCustomer && actionsMenu}
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              isFromCustomer
                                ? "bg-white text-gray-900 rounded-tl-none"
                                : "bg-teal-500 text-white rounded-tr-none"
                            }`}
                            data-testid={`${messageTestId}-bubble`}
                          >
                            {msg.media_url && (() => {
                              const rawSrc = String(msg.media_url);
                              const src = rawSrc.startsWith("http") ? rawSrc : `${BACKEND_URL}${rawSrc}`;
                              const mt = msg.media_mime || "";
                              const mType = msg.message_type || msg.type;
                              const isImage = mt.startsWith("image/") || mType === "image" || mType === "sticker";
                              const isVideo = mt.startsWith("video/") || mType === "video";
                              if (isImage) {
                                return (
                                  <a href={src} target="_blank" rel="noopener noreferrer" data-testid={`${messageTestId}-media-image-link`}>
                                    <img src={src} alt="Bild" className="rounded-lg max-w-full max-h-64 object-cover mb-1 cursor-pointer" data-testid={`${messageTestId}-media-image`} />
                                  </a>
                                );
                              }
                              if (isVideo) {
                                return (
                                  <video src={src} controls className="rounded-lg max-w-full max-h-64 mb-1" data-testid={`${messageTestId}-media-video`} />
                                );
                              }
                              if (mt.startsWith("audio/") || mType === "audio") {
                                return (
                                  <audio src={src} controls className="mb-1 w-64 max-w-full" data-testid={`${messageTestId}-media-audio`} />
                                );
                              }
                              return (
                                <a href={src} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 underline mb-1 ${isFromCustomer ? "text-teal-700" : "text-white"}`} data-testid={`${messageTestId}-media-doc`}>
                                  <FileText className="h-4 w-4 shrink-0" /> {msg.media_filename || "Dokument öffnen"}
                                </a>
                              );
                            })()}
                            {editingMsgId === (msg.id || msg.message_id) ? (
                              <div className="space-y-1" data-testid={`${messageTestId}-edit-box`}>
                                <textarea
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  rows={2}
                                  autoFocus
                                  className="w-full text-sm text-gray-900 bg-white rounded-md p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-teal-400"
                                  data-testid={`${messageTestId}-edit-input`}
                                />
                                <div className="flex gap-2 justify-end">
                                  <button
                                    onClick={cancelEditMessage}
                                    className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                                    data-testid={`${messageTestId}-edit-cancel`}
                                  >
                                    Abbrechen
                                  </button>
                                  <button
                                    onClick={() => saveEditMessage(msg)}
                                    className="text-xs px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
                                    data-testid={`${messageTestId}-edit-save`}
                                  >
                                    Speichern
                                  </button>
                                </div>
                              </div>
                            ) : (msg.content || msg.body) ? (
                              <p className="text-sm whitespace-pre-wrap break-words" data-testid={`${messageTestId}-text`}>
                                {msg.content || msg.body}
                              </p>
                            ) : null}
                            {!isFromCustomer && msg.auto_translated_to && msg.original_content && (
                              <p className="text-xs italic mt-1 text-teal-100/80 border-t border-teal-400/40 pt-1" data-testid={`${messageTestId}-original-text`}>
                                Original: {msg.original_content}
                              </p>
                            )}
                            {isFromCustomer && msg.translation_de && (
                              <div className="mt-1 pt-1 border-t border-gray-200" data-testid={`${messageTestId}-translation-de`}>
                                <p className="text-xs text-gray-600 italic">🌍 {msg.translation_de}</p>
                              </div>
                            )}
                            {(msg.transcription_failed || (msg.message_type === 'audio' && String(msg.content || msg.body || '').includes('konnte nicht transkribiert'))) && (
                              <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-300" data-testid={`${messageTestId}-transcription-failed`}>
                                <div className="flex items-center gap-1 text-amber-700 text-xs font-semibold mb-2">
                                  ⚠️ Sprachnachricht nicht transkribiert
                                </div>
                                {audioUrls[msg.id] ? (
                                  <audio controls src={audioUrls[msg.id]} className="w-full h-8 mb-2" data-testid={`${messageTestId}-audio-player`} />
                                ) : (
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 text-xs mr-2 border-amber-400 text-amber-800"
                                    disabled={audioLoading === msg.id}
                                    onClick={() => loadVoiceAudio(msg.id)}
                                    data-testid={`${messageTestId}-listen-btn`}
                                  >
                                    {audioLoading === msg.id ? 'Lädt…' : '▶️ Anhören'}
                                  </Button>
                                )}
                                <Button
                                  size="sm" variant="outline"
                                  className="h-7 text-xs border-amber-400 text-amber-800"
                                  disabled={retranscribing === msg.id}
                                  onClick={() => retranscribeMessage(msg.id)}
                                  data-testid={`${messageTestId}-retranscribe-btn`}
                                >
                                  {retranscribing === msg.id ? 'Transkribiere…' : '🔄 Erneut transkribieren'}
                                </Button>
                              </div>
                            )}
                            <div className={`flex items-center gap-1 mt-1 text-xs ${
                              isFromCustomer ? "text-gray-500" : "text-teal-100"
                            }`} data-testid={`${messageTestId}-meta`}>
                              {msg.is_ai_generated && (
                                <Badge variant="outline" className="h-4 text-[10px] px-1 mr-1" data-testid={`${messageTestId}-ai-badge`}>
                                  KI
                                </Badge>
                              )}
                              {msg.auto_translated_to && (
                                <Badge variant="outline" className="h-4 text-[10px] px-1 mr-1 border-teal-300 text-teal-100" data-testid={`${messageTestId}-translated-badge`}>
                                  🌍 übersetzt {msg.auto_translated_to.toUpperCase()}
                                </Badge>
                              )}
                              <span data-testid={`${messageTestId}-time`}>{formatTime(msg.timestamp)}</span>
                              {msg.edited && (
                                <span className="italic opacity-70" data-testid={`${messageTestId}-edited`}>· bearbeitet</span>
                              )}
                              {!isFromCustomer && getStatusIcon(msg.status)}
                            </div>
                          </div>
                          {isFromCustomer && actionsMenu}
                        </div>
                      </div>
                    );
                    })}
                    <div ref={messagesEndRef} data-testid="whatsapp-message-list-end" />
                  </div>
                </ScrollArea>

                {showScrollToLatest && (
                  <div className="absolute bottom-4 right-6 z-20" data-testid="whatsapp-jump-to-latest-container">
                    <Button
                      type="button"
                      size="sm"
                      onClick={jumpToLatestMessages}
                      className="rounded-full shadow-lg bg-teal-600 hover:bg-teal-700 text-white"
                      data-testid="whatsapp-jump-to-latest-button"
                    >
                      <ChevronDown className="h-4 w-4 mr-1" />
                      {pendingNewMessages ? "Neue Nachrichten" : "Zum Ende"}
                    </Button>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="bg-white p-4 border-t" data-testid="whatsapp-message-input-area">
                {pendingAudio && (
                  <div className="flex items-center gap-2 max-w-4xl mx-auto mb-2 bg-zinc-50 border border-zinc-200 rounded-lg p-2" data-testid="whatsapp-voice-review">
                    <audio controls src={pendingAudio.url} className="h-9 flex-1 min-w-0" data-testid="whatsapp-voice-preview" />
                    <Button
                      size="sm"
                      onClick={sendVoiceMessage}
                      disabled={sendingVoice}
                      className="bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                      title="Aufnahme als WhatsApp-Sprachnachricht senden"
                      data-testid="whatsapp-voice-send"
                    >
                      {sendingVoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Als Sprachnachricht senden</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={transcribePendingAudio}
                      disabled={transcribing || sendingVoice}
                      className="shrink-0 border-zinc-300 text-zinc-600 hover:bg-zinc-100"
                      title="Aufnahme in Text umwandeln"
                      data-testid="whatsapp-voice-transcribe"
                    >
                      {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : "In Text umwandeln"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={discardPendingAudio}
                      disabled={sendingVoice}
                      className="shrink-0 text-zinc-500 hover:text-red-600"
                      title="Aufnahme verwerfen"
                      data-testid="whatsapp-voice-discard"
                    >
                      Verwerfen
                    </Button>
                  </div>
                )}
                {pendingMediaFile && (
                  <div className="max-w-4xl mx-auto mb-2 bg-zinc-50 border border-zinc-200 rounded-lg p-2" data-testid="whatsapp-media-preview">
                    <div className="flex items-start gap-3">
                      {pendingMediaFile.kind === "image" ? (
                        <img src={pendingMediaFile.url} alt="Vorschau" className="h-20 w-20 object-cover rounded-lg shrink-0" data-testid="whatsapp-media-preview-image" />
                      ) : pendingMediaFile.kind === "video" ? (
                        <video src={pendingMediaFile.url} className="h-20 w-20 object-cover rounded-lg shrink-0 bg-black" />
                      ) : (
                        <div className="h-20 w-20 flex flex-col items-center justify-center bg-zinc-100 rounded-lg shrink-0 text-zinc-500 p-1">
                          <FileText className="h-6 w-6" />
                          <span className="text-[10px] mt-1 truncate w-full text-center">{pendingMediaFile.name}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <Input
                          value={mediaCaption}
                          onChange={(e) => setMediaCaption(e.target.value)}
                          placeholder="Bildunterschrift hinzufügen (optional)…"
                          className="mb-2"
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendPendingMediaFile(); } }}
                          data-testid="whatsapp-media-caption-input"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={sendPendingMediaFile} disabled={uploadingMedia} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="whatsapp-media-send">
                            {uploadingMedia ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Senden</>}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={discardPendingMediaFile} disabled={uploadingMedia} className="text-zinc-500 hover:text-red-600" data-testid="whatsapp-media-discard">
                            Verwerfen
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex gap-2 max-w-4xl mx-auto">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,video/*"
                    className="hidden"
                    onChange={handleMediaSelected}
                    data-testid="whatsapp-media-file-input"
                  />
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingMedia || sending}
                    variant="outline"
                    className="self-end border-zinc-300 text-zinc-600 hover:bg-zinc-50"
                    title="Foto, PDF oder Video senden"
                    data-testid="whatsapp-attach-button"
                  >
                    {uploadingMedia ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  </Button>
                  <Textarea
                    placeholder="Nachricht eingeben..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    className="flex-1 resize-none"
                    rows={2}
                    data-testid="whatsapp-message-input"
                  />
                  <Button
                    onClick={toggleRecording}
                    disabled={transcribing || sending}
                    variant="outline"
                    className={`self-end ${recording ? 'border-red-400 text-red-600 bg-red-50 animate-pulse' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'}`}
                    title={recording ? 'Aufnahme beenden' : 'Sprachnachricht aufnehmen'}
                    data-testid="whatsapp-voice-record-button"
                  >
                    {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        disabled={!newMessage.trim() || translating || sending}
                        variant="outline"
                        className="self-end border-sky-300 text-sky-600 hover:bg-sky-50 hover:text-sky-700"
                        title="Text übersetzen"
                        data-testid="whatsapp-translate-button"
                      >
                        {translating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" data-testid="whatsapp-translate-menu">
                      {['Englisch', 'Deutsch', 'Französisch', 'Italienisch', 'Spanisch', 'Portugiesisch'].map((lang) => (
                        <DropdownMenuItem key={lang} onClick={() => translateText(lang)} data-testid={`translate-to-${lang.toLowerCase()}`}>
                          🌐 Auf {lang} übersetzen
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    onClick={async () => {
                      const phone = selectedChat?.chat_id;
                      if (!phone) { toast.error('Kein Chat ausgewählt'); return; }
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${BACKEND_URL}/api/onboarding/invite`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ phone, channels: ['whatsapp'], preferred_language: 'de' }),
                        });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data?.detail || 'Fehler');
                        if (data?.sent?.whatsapp) toast.success('Onboarding-Link per WhatsApp gesendet');
                        else { toast.success('Link erstellt'); if (data?.onboarding_link) { try { await navigator.clipboard.writeText(data.onboarding_link); } catch {} } }
                      } catch (e) {
                        toast.error(e.message || 'Senden fehlgeschlagen');
                      }
                    }}
                    disabled={sending}
                    variant="outline"
                    className="self-end border-teal-300 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
                    title="Onboarding-Link an diesen Chat senden (Kunde trägt Daten selbst ein)"
                    data-testid="whatsapp-send-onboarding-link-button"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={improveText}
                    disabled={!newMessage.trim() || improving || sending}
                    variant="outline"
                    className="self-end border-violet-300 text-violet-600 hover:bg-violet-50 hover:text-violet-700"
                    title="Text mit KI korrigieren: Rechtschreibung, freundlicher Ton + Emojis"
                    data-testid="whatsapp-improve-text-button"
                  >
                    {improving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  </Button>
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="self-end"
                    data-testid="whatsapp-message-send-button"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center" data-testid="whatsapp-empty-state">
              <div className="text-center text-gray-400">
                <MessageCircle className="h-16 w-16 mx-auto mb-4" />
                <p className="text-lg font-medium" data-testid="whatsapp-empty-state-title">Wählen Sie einen Chat aus</p>
                <p className="text-sm" data-testid="whatsapp-empty-state-subtitle">um Nachrichten anzuzeigen</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
